import { useState } from "react";
import { useAiFeedback } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ThumbsUp, ThumbsDown } from "lucide-react";

interface AIFeedbackProps {
  aiUsageId: number | null | undefined;
  className?: string;
}

export function AIFeedback({ aiUsageId, className }: AIFeedbackProps) {
  const [rating, setRating] = useState<"up" | "down" | null>(null);
  const [comment, setComment] = useState("");
  const [commentSent, setCommentSent] = useState(false);
  const mutation = useAiFeedback();

  if (!aiUsageId) return null;

  const submitRating = (next: "up" | "down") => {
    if (rating) return;
    setRating(next);
    mutation.mutate({ data: { aiUsageId, rating: next } });
  };

  const submitComment = () => {
    if (!rating || commentSent || !comment.trim()) return;
    mutation.mutate(
      { data: { aiUsageId, rating, comment: comment.trim().slice(0, 500) } },
      { onSuccess: () => setCommentSent(true) },
    );
  };

  return (
    <div className={`mt-2 text-xs text-gray-500 ${className ?? ""}`}>
      <div className="flex items-center gap-2">
        <span>Was this helpful?</span>
        <button
          type="button"
          onClick={() => submitRating("up")}
          disabled={!!rating}
          aria-label="Thumbs up"
          className={`p-1 rounded hover:bg-gray-100 disabled:hover:bg-transparent ${
            rating === "up" ? "text-green-600" : "text-gray-400"
          }`}
        >
          <ThumbsUp className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => submitRating("down")}
          disabled={!!rating}
          aria-label="Thumbs down"
          className={`p-1 rounded hover:bg-gray-100 disabled:hover:bg-transparent ${
            rating === "down" ? "text-red-600" : "text-gray-400"
          }`}
        >
          <ThumbsDown className="w-3.5 h-3.5" />
        </button>
        {rating && !commentSent && rating === "up" && (
          <span className="text-green-600">Thanks!</span>
        )}
        {commentSent && <span className="text-green-600">Thanks for the detail!</span>}
      </div>
      {rating === "down" && !commentSent && (
        <div className="mt-1.5 flex items-start gap-2">
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="What went wrong? (optional)"
            maxLength={500}
            rows={1}
            className="text-xs min-h-[32px] py-1.5"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!comment.trim() || mutation.isPending}
            onClick={submitComment}
          >
            Send
          </Button>
        </div>
      )}
    </div>
  );
}
